<template>
  <v-container
    fluid
    class="main-container responsive-height change-password-page"
    :class="{ 'change-password-page--dark': $vuetify.theme.dark }"
  >
    <div class="page-heading">
      <h1>Change MT5 Password</h1>
      <div class="breadcrumbs">
        <span>Home</span><v-icon small>mdi-chevron-right</v-icon>
        <span>Manage Trading Account</span><v-icon small>mdi-chevron-right</v-icon>
        <span>Change MT5 Password</span>
      </div>
    </div>

    <v-card class="password-card" elevation="0">
      <div class="card-heading">
        <div class="heading-icon"><v-icon>mdi-lock-outline</v-icon></div>
        <div>
          <h2>Change MT5 Trading Account Password</h2>
          <p>Keep your trading account secure by using a strong password.</p>
        </div>
      </div>

      <v-row class="form-grid">
        <!-- Account Selection -->
        <v-col cols="12">
          <fieldset class="form-fieldset">
            <legend>Select Account No</legend>
            <v-select
              v-model="selectedAccount"
              :items="userleverages"
              :item-text="
                (item) =>
                  `${item.trading_account_no} (${item.tradingaccounttypes?.account_type_display})`
              "
              item-value="trading_account_no"
              label="MT5 Account"
              single-line
              solo
              hide-details
              append-icon="mdi-chevron-down"
              class="custom-select"
              :readonly="!isEditable"
            >
              <template v-slot:prepend-inner>
                <span class="field-icon"><v-icon>mdi-account-outline</v-icon></span>
              </template>
            </v-select>
          </fieldset>
        </v-col>

        <!-- Password Type Selection (NEW) -->
        <v-col cols="12">
          <fieldset class="form-fieldset">
            <legend>Password Type</legend>
            <v-select
              v-model="passwordType"
              :items="passwordTypes"
              item-text="text"
              item-value="value"
              label="Select Password Type"
              single-line
              solo
              hide-details
              append-icon="mdi-chevron-down"
              class="custom-select"
              :disabled="!selectedAccount"
            >
              <template v-slot:prepend-inner>
                <span class="field-icon"><v-icon>
                  {{ passwordType === 'master' ? 'mdi-key' : passwordType === 'investor' ? 'mdi-key-variant' : 'mdi-key-outline' }}
                </v-icon></span>
              </template>
            </v-select>
          </fieldset>
        </v-col>

        <!-- Info Alert -->
        <v-col cols="12" v-if="passwordType">
          <v-alert
            :type="passwordType === 'master' ? 'info' : 'warning'"
            dense
            outlined
            border="left"
            class="password-info mb-0"
          >
            <div class="text-body-2">
              <strong v-if="passwordType === 'master'">Master Password:</strong>
              <strong v-else>Investor Password:</strong>
              {{ passwordType === 'master' 
                ? 'Used for full trading access and account management' 
                : 'Used for read-only access to view trades and account status' 
              }}
            </div>
          </v-alert>
        </v-col>

        <!-- New Password -->
        <v-col cols="12">
          <fieldset class="form-fieldset">
            <legend>New {{ passwordType === 'master' ? 'Master' : passwordType === 'investor' ? 'Investor' : '' }} Password</legend>
            <v-text-field
              v-model="newPassword"
              :label="`New ${passwordType === 'master' ? 'Master' : passwordType === 'investor' ? 'Investor' : ''} Password`"
              :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
              :type="showPassword ? 'text' : 'password'"
              @click:append="showPassword = !showPassword"
              single-line
              solo
              hide-details
              class="custom-select"
              :disabled="!passwordType"
            >
              <template v-slot:prepend-inner>
                <span class="field-icon"><v-icon>mdi-key-outline</v-icon></span>
              </template>
            </v-text-field>
          </fieldset>
        </v-col>

        <!-- Password Rules -->
        <v-row class="password-rules mx-0">
          <v-col
            v-for="(rule, index) in passwordRules"
            :key="index"
            cols="auto"
            class="pa-0 mr-3 mb-3"
          >
            <v-chip small outlined :class="{ 'rule-met': rule.met }">
              <v-icon small class="mr-1">{{
                rule.met ? "mdi-check" : "mdi-close"
              }}</v-icon>
              {{ rule.text }}
            </v-chip>
          </v-col>
        </v-row>

        <!-- Confirm Password -->
        <v-col cols="12">
          <fieldset class="form-fieldset">
            <legend>Confirm Password</legend>
            <v-text-field
              v-model="confirmPassword"
              :label="`Confirm new ${passwordType === 'master' ? 'master' : passwordType === 'investor' ? 'investor' : ''} password`"
              :append-icon="showConfirmPassword ? 'mdi-eye' : 'mdi-eye-off'"
              :type="showConfirmPassword ? 'text' : 'password'"
              @click:append="showConfirmPassword = !showConfirmPassword"
              single-line
              solo
              hide-details
              class="custom-select"
              :disabled="!newPassword"
            >
              <template v-slot:prepend-inner>
                <span class="field-icon"><v-icon>mdi-key-outline</v-icon></span>
              </template>
            </v-text-field>
          </fieldset>
        </v-col>

        <!-- Error Message & Submit Button -->
        <v-col cols="12" class="submit-section">
          <v-alert v-if="errorMessage" type="error" class="mb-4">
            {{ errorMessage }}
          </v-alert>

          <v-btn
            color="primary"
            large
            tile
            elevation="0"
            class="save-password-btn text-capitalize"
            @click="savePassword"
            :loading="loading" 
            :disabled="loading || !isFormValid"
          >
            <v-icon left>mdi-content-save-outline</v-icon>
            Save {{ passwordType === 'master' ? 'Master' : passwordType === 'investor' ? 'Investor' : '' }} Password
          </v-btn>
        </v-col>
      </v-row>
    </v-card>
  </v-container>
</template>

<script>
import { LEVERAGE__ACTIONS } from "@/store/action-types";
import { mapActions, mapState } from "vuex";
import toast from "@/services/Notification";

export default {
  data: () => ({
    isEditable: true,
    selectedAccount: "",
    passwordType: null, // 'master' or 'investor'
    currentPassword: "",
    loading: false,
    newPassword: "",
    confirmPassword: "",
    showPassword: false,
    showConfirmPassword: false,
    errorMessage: "",
    
    passwordTypes: [
      { 
        text: "Master Password (Full Access)", 
        value: "master",
        icon: "mdi-key"
      },
      { 
        text: "Investor Password (Read-Only)", 
        value: "investor",
        icon: "mdi-key-variant"
      },
    ],
    
    passwordRules: [
      { text: "8 Characters", met: false },
      { text: "1 Lowercase", met: false },
      { text: "1 Uppercase", met: false },
      { text: "1 Digit", met: false },
      { text: "1 Special Character", met: false },
    ],
  }),

  computed: {
    ...mapState("leverage", ["userleverages"]),

    isFormValid() {
      return (
        this.selectedAccount &&
        this.passwordType &&
        this.newPassword &&
        this.confirmPassword &&
        this.newPassword === this.confirmPassword &&
        this.passwordRules.every(rule => rule.met)
      );
    },
  },

  watch: {
    newPassword() {
      this.validatePassword();
    },
    
    selectedAccount(newAccount) {
      const account = this.userleverages.find(
        (item) => item.trading_account_no === newAccount
      );
      if (account) {
        this.currentLeverage = `1:${account.leverage}`;
      } else {
        this.currentLeverage = "";
      }
      
      // Reset password type when account changes
      if (!this.isEditable) {
        this.passwordType = null;
      }
    },
  },

  methods: {
    ...mapActions("leverage", [
      LEVERAGE__ACTIONS.USERLEVERAGE,
      LEVERAGE__ACTIONS.UPDATEMT5PASSWORD,
    ]),

    validatePassword() {
      this.passwordRules[0].met = this.newPassword.length >= 8;
      this.passwordRules[1].met = /[a-z]/.test(this.newPassword);
      this.passwordRules[2].met = /[A-Z]/.test(this.newPassword);
      this.passwordRules[3].met = /\d/.test(this.newPassword);
      this.passwordRules[4].met = /[!@#$%^&*(),.?":{}|<>]/.test(
        this.newPassword
      );
    },

    async savePassword() {
      this.loading = true;
      this.errorMessage = "";

      try {
        // Validate passwords match
        if (this.newPassword !== this.confirmPassword) {
          this.errorMessage = "Passwords do not match!";
          return;
        }

        // Validate all password rules are met
        if (!this.passwordRules.every(rule => rule.met)) {
          this.errorMessage = "Password does not meet all requirements!";
          return;
        }

        // Prepare payload based on password type
        const payload = {
          login_acc: this.selectedAccount,
        };

        if (this.passwordType === 'master') {
          payload.new_password = this.newPassword;
        } else if (this.passwordType === 'investor') {
          payload.new_investor_password = this.newPassword;
        }

        // Call appropriate action based on password type
        if (this.passwordType === 'master') {
          await this[LEVERAGE__ACTIONS.UPDATEMT5PASSWORD](payload);
        } else if (this.passwordType === 'investor') {
          // You may need to create a new action for investor password
          // For now, using the same action with different payload
          await this[LEVERAGE__ACTIONS.UPDATEMT5PASSWORD](payload);
        }

        toast.success(
          `${this.passwordType === 'master' ? 'Master' : 'Investor'} password changed successfully!`
        );

        // Reset form
        this.resetForm();

      } catch (error) {
        console.error('Trading Account Password change error:', error);
        this.errorMessage = error.response?.data?.message || "An error occurred while updating the password.";
        toast.error(this.errorMessage);
      } finally {
        this.loading = false;
      }
    },

    resetForm() {
      if (this.isEditable) {
        this.selectedAccount = "";
      }
      this.passwordType = null;
      this.newPassword = "";
      this.confirmPassword = "";
      this.showPassword = false;
      this.showConfirmPassword = false;
      this.errorMessage = "";
    },
  },

  async mounted() {
    const account = this.$route.query.account;
    await this[LEVERAGE__ACTIONS.USERLEVERAGE]();

    if (account) {
      this.selectedAccount = account;
      this.isEditable = false;
    }
  },
};
</script>

<style scoped>
.change-password-page {
  --page-text: #172033;
  --muted-text: #626973;
  --card-bg: #ffffff;
  --field-bg: #fbfaf6;
  --field-border: #e0e3e7;
  --purple: #d99a00;
  padding: 24px 30px 48px;
  background: #f7f8fa;
}

.change-password-page--dark {
  --page-text: #f4f6fb;
  --muted-text: #afb6bf;
  --card-bg: linear-gradient(120deg, #07111b 0%, #08131d 65%, #061019 100%);
  --field-bg: rgba(8, 19, 30, 0.82);
  --field-border: #3d351e;
  --purple: #ffc400;
  background: #030b13;
}

.page-heading { margin-bottom: 28px; }
.page-heading h1 {
  color: var(--page-text);
  font-size: 25px;
  line-height: 1.25;
  margin: 0 0 10px;
}
.breadcrumbs { display: flex; align-items: center; gap: 10px; color: var(--muted-text); font-size: 14px; }
.breadcrumbs .v-icon { color: var(--muted-text); }
.breadcrumbs span:last-child { color: var(--purple); }

.password-card {
  color: var(--page-text);
  background: var(--card-bg) !important;
  border: 1px solid var(--field-border) !important;
  border-radius: 9px !important;
  max-width: 780px;
  padding: 22px 24px 25px;
  width: 55%;
  overflow: hidden;
}
.card-heading { display: flex; align-items: center; gap: 14px; margin-bottom: 17px; }
.heading-icon {
  align-items: center;
  background: linear-gradient(145deg, #7a5900, #4d3900);
  border: 1px solid rgba(255, 196, 0, 0.38);
  border-radius: 9px;
  display: flex;
  flex: 0 0 44px;
  height: 44px;
  justify-content: center;
}
.heading-icon .v-icon { color: #ffc400; font-size: 24px; }
.card-heading h2 { font-size: 17px; line-height: 1.3; margin: 0 0 2px; color: var(--page-text); }
.card-heading p { color: var(--muted-text); font-size: 12px; margin: 0; }
.form-grid { margin: 0 -8px; }
.form-grid > .col { padding: 8px; }
.form-fieldset { border: 0; margin: 0; padding: 0; }
.form-fieldset legend { color: var(--page-text); font-size: 13px; font-weight: 600; margin-bottom: 5px; }

.change-password-page ::v-deep .custom-select.v-text-field.v-text-field--solo .v-input__control { min-height: 46px; }
.change-password-page ::v-deep .custom-select.v-text-field.v-text-field--solo .v-input__slot {
  background: var(--field-bg) !important;
  border: 1px solid var(--field-border);
  border-radius: 8px;
  box-shadow: none !important;
  min-height: 46px;
  padding: 0 13px;
}
.change-password-page ::v-deep .custom-select .v-label,
.change-password-page ::v-deep .custom-select input,
.change-password-page ::v-deep .custom-select .v-select__selection { color: var(--page-text) !important; }
.change-password-page ::v-deep .custom-select .v-icon { color: var(--purple) !important; }
.change-password-page ::v-deep .custom-select .v-label { color: var(--muted-text) !important; }
.field-icon {
  align-items: center;
  background: rgba(227, 166, 0, 0.1);
  border-radius: 50%;
  display: flex;
  height: 28px;
  justify-content: center;
  margin-right: 9px;
  width: 28px;
}
.change-password-page .field-icon .v-icon { color: var(--purple) !important; font-size: 18px; }

.password-info {
  min-height: 48px;
  color: var(--page-text) !important;
  background: rgba(217, 154, 0, 0.04) !important;
  border-color: rgba(217, 154, 0, 0.48) !important;
  border-radius: 8px;
}
.password-info ::v-deep .v-alert__border { background-color: var(--purple) !important; }
.password-info ::v-deep .v-alert__icon { color: var(--purple) !important; }
.password-info ::v-deep .v-alert__content { align-self: center; color: var(--page-text) !important; }
.password-info .text-body-2 { color: var(--page-text) !important; line-height: 1.5; }
.password-info .text-body-2 strong { color: var(--purple); }

.change-password-page--dark .password-info {
  background: rgba(227, 166, 0, 0.07) !important;
  border-color: rgba(227, 166, 0, 0.42) !important;
}
.password-rules { margin-top: 8px !important; padding: 1px 8px 3px; }
.password-rules .v-chip {
  background: transparent !important;
  border-color: rgba(217, 154, 0, 0.55);
  color: var(--muted-text);
  font-size: 13px;
  height: 30px;
  padding: 0 10px;
}
.password-rules .v-chip.rule-met { border-color: #00a779; color: #00a779; }
.password-rules .rule-met .v-icon { color: #00c98b; }
.submit-section { padding-top: 18px !important; }
.save-password-btn {
  background: linear-gradient(180deg, #ffc826, #e4a500) !important;
  border-radius: 6px;
  color: #17130a !important;
  font-weight: 600;
  height: 46px !important;
  letter-spacing: 0;
  padding: 0 24px !important;
}

.save-password-btn ::v-deep .v-icon,
.save-password-btn ::v-deep .v-btn__content {
  color: #17130a !important;
}

@media (max-width: 1400px) {
  .password-card { width: 65%; }
}

@media (max-width: 1100px) {
  .password-card { max-width: none; width: 100%; }
}

@media (max-width: 600px) {
  .change-password-page { padding: 18px 14px 36px; }
  .password-card { padding: 22px 16px 24px; }
  .card-heading { align-items: flex-start; }
  .card-heading h2 { font-size: 18px; }
  .breadcrumbs { gap: 5px; font-size: 12px; flex-wrap: wrap; }
  .save-password-btn { width: 100%; }
}
</style>
